import { IApiAxiosError } from "@/api";
import { createTopic, ITopicCreate } from "@/api/topics";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import { Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core/useDialogs";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

interface NewTopicFormValues {
    name: string;
    description: string;
}

export const NewTopicDialog = ({ open, onClose }: DialogProps) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        reset,
        setError,
        formState: { errors },
    } = useForm<NewTopicFormValues>({
        defaultValues: {
            name: "",
            description: "",
        },
    });

    const createMutation = useMutation({
        mutationFn: (data: ITopicCreate) => createTopic(data),
        onSuccess: (topic) => {
            handleClose();
            queryClient.invalidateQueries({
                queryKey: ["admin-topics"],
            });

            // Navigate to the new topic
            navigate(`admin/topics/${topic.id}/details`);
        },
        onError: (error) => {
            const axiosError = error as IApiAxiosError;
            setError("name", {
                type: "manual",
                message:
                    axiosError.response?.data.detail ||
                    "An error occurred while creating the topic.",
            });
        },
    });

    const handleClose = () => {
        onClose();
        reset();
    };

    const onSubmit = (data: NewTopicFormValues) => {
        createMutation.mutate({
            name: data.name.trim(),
            description: data.description.trim() || "",
        });
    };

    const axiosError = createMutation.error as IApiAxiosError;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Create a New Topic"
            isLoading={createMutation.isPending}
            actions={[
                {
                    label: "Create",
                    onClick: handleSubmit(onSubmit),
                    variant: "contained",
                    color: "primary",
                    disabled: createMutation.isPending,
                },
            ]}
            footerText={
                axiosError && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "0.75rem" }}
                    >
                        {axiosError.response?.data.detail ||
                            "An error occurred while creating the topic."}
                    </Typography>
                )
            }
        >
            <Stack spacing={2}>
                <Controller
                    name="name"
                    control={control}
                    rules={{
                        validate: (value) =>
                            value.trim() !== "" || "Name cannot be empty",
                    }}
                    render={({ field: { ref, ...field } }) => (
                        <TextField
                            {...field}
                            inputRef={ref}
                            label="Name"
                            error={!!errors.name}
                            helperText={errors.name?.message}
                            slotProps={{ htmlInput: { autoFocus: true } }}
                            required
                            sx={{ width: "100%" }}
                        />
                    )}
                />
                <Controller
                    name="description"
                    control={control}
                    render={({ field: { ref, ...field } }) => (
                        <TextField
                            {...field}
                            inputRef={ref}
                            label="Description"
                            helperText={`${field.value.length}/1000`}
                            multiline
                            minRows={3}
                            slotProps={{
                                htmlInput: { maxLength: 1000 },
                                formHelperText: {
                                    sx: {
                                        color:
                                            field.value.length >= 1000
                                                ? "error.main"
                                                : undefined,
                                    },
                                },
                            }}
                            sx={{ width: "100%" }}
                        />
                    )}
                />
            </Stack>
        </Modal>
    );
};
